import TinyColor from "@ctrl/tinycolor";

export interface ButtonColors {
  background?: string | null;
  border?: string | null;
  text?: string | null;
  backgroundHover?: string | null;
  textHover?: string | null;
}
export interface ButtonStyle {
  font?: string | null;
  cornerRadius?: number | null;
  borderWidth?: number | null;
  padding?: number | null;
}
export interface ButtonVariant {
  colors?: ButtonColors | null;
  style?: ButtonStyle | null;
}

function darken(color: string, amount: number): string {
  return TinyColor(color).darken(amount).toHexString();
}

const HOVER_DARKEN_AMOUNT = 8;

export function resolveBackgroundHover(
  colors: ButtonColors | null | undefined,
): string | undefined {
  const explicit = colors?.backgroundHover || undefined;
  if (explicit) {
    return explicit;
  }
  const bg = colors?.background || undefined;
  return bg ? darken(bg, HOVER_DARKEN_AMOUNT) : undefined;
}

export function resolveTextHover(
  colors: ButtonColors | null | undefined,
): string | undefined {
  return colors?.textHover || colors?.text || undefined;
}

export function buildButtonHoverTextRules(
  s: string,
  hierarchy: "primary" | "secondary",
  colors: ButtonColors | null | undefined,
): string[] {
  // The design system's primary button :hover doesn't read fg_hover, and the
  // secondary path only reads it for theme-normal. Emit an explicit override so
  // an explicit textHover takes effect. !important beats the design system's
  // own :hover color rule when both selectors match the same element.
  if (!colors?.textHover) {
    return [];
  }
  return [
    `${s}[data-hierarchy="${hierarchy}"]:hover { color: ${colors.textHover} !important; }`,
  ];
}

export function buildSecondaryHoverBackgroundRule(
  s: string,
  colors: ButtonColors | null | undefined,
): string[] {
  // The design system's secondary :hover background reads
  // --redo-colors-background-bg-primary_hover, not secondary-bg_hover. Emit
  // an explicit override so the resolved hover bg actually paints.
  const bgHover = resolveBackgroundHover(colors);
  if (!bgHover) {
    return [];
  }
  return [
    `${s}[data-hierarchy="secondary"]:hover { background-color: ${bgHover} !important; }`,
  ];
}

export function buildButtonColorVarDeclarations(
  hierarchy: "primary" | "secondary",
  colors: ButtonColors | null | undefined,
): string[] {
  if (!colors) {
    return [];
  }
  const declarations: string[] = [];

  const bg = colors.background || undefined;
  const border = colors.border || undefined;
  const text = colors.text || undefined;

  const h = hierarchy;
  const prefix = `--redo-component-colors-components-buttons-${h}-button-${h}`;

  const bgHover = resolveBackgroundHover(colors);
  const textHover = resolveTextHover(colors);

  if (bg) {
    declarations.push(`${prefix}-bg: ${bg};`);
    declarations.push(
      `--redo-component-colors-components-buttons-${h}-bg_pressed: ${darken(bg, 15)};`,
    );
  }
  if (bgHover) {
    declarations.push(`${prefix}-bg_hover: ${bgHover};`);
  }
  if (border) {
    declarations.push(`${prefix}-border: ${border};`);
    declarations.push(`${prefix}-border_hover: ${darken(border, 8)};`);
  }
  if (text) {
    declarations.push(`${prefix}-fg: ${text};`);
  }
  if (textHover) {
    declarations.push(`${prefix}-fg_hover: ${textHover};`);
  }

  return declarations;
}

export function buildTertiaryColorRules(
  s: string,
  colors: ButtonColors | null | undefined,
): string[] {
  if (!colors) {
    return [];
  }
  const bg = colors.background || undefined;
  const border = colors.border || undefined;
  const text = colors.text || undefined;

  const props: string[] = [];
  if (bg) {
    props.push(`background-color: ${bg};`);
  }
  if (border) {
    props.push(`outline: 1px solid ${border};`);
  }
  if (text) {
    props.push(`color: ${text};`);
  }
  if (props.length === 0) {
    return [];
  }

  const bgHover = resolveBackgroundHover(colors);
  const textHover = resolveTextHover(colors);

  const baseRule = `${s}[data-hierarchy="tertiary"] { ${props.join(" ")} }`;

  const hoverProps: string[] = [];
  if (bgHover) {
    hoverProps.push(`background-color: ${bgHover};`);
  }
  if (textHover && textHover !== text) {
    hoverProps.push(`color: ${textHover};`);
  }
  const hoverRule =
    hoverProps.length > 0
      ? `${s}[data-hierarchy="tertiary"]:hover { ${hoverProps.join(" ")} }`
      : undefined;

  const activeRule = bg
    ? `${s}[data-hierarchy="tertiary"]:active { background-color: ${darken(bg, 15)}; }`
    : undefined;

  return [baseRule, hoverRule, activeRule].filter(
    (rule): rule is string => rule !== undefined,
  );
}

export function buildButtonStyleRules(
  s: string,
  hierarchy: "primary" | "secondary" | "tertiary",
  variant: ButtonVariant | null | undefined,
): string[] {
  const style = variant?.style;
  if (!style) {
    return [];
  }

  const props: string[] = [];
  if (style.font) {
    props.push(`font-family: ${style.font};`);
  }
  if (typeof style.cornerRadius === "number") {
    props.push(`border-radius: ${style.cornerRadius}px;`);
  }
  if (typeof style.borderWidth === "number") {
    // RedoButton strokes with `outline`, not `border`.
    props.push(`outline-width: ${style.borderWidth}px;`);
  }
  if (typeof style.padding === "number") {
    props.push(`padding: ${style.padding}px;`);
  }
  if (props.length === 0) {
    return [];
  }

  const buttonStyleRule = `${s}[data-hierarchy="${hierarchy}"] { ${props.join(" ")} }`;

  return [buttonStyleRule];
}
